<template>
  <div>
    <div class="backdrop" @click="$emit('dismiss')"></div>
    <div
      class="inbox-content-modal px-2 py-3"
      :class="{ 'd-flex': loadingState !== LoadingState.LOADED }"
    >
      <div class="flex-center flex-grow-1" v-if="loadingState === LoadingState.LOADING">
        <spinner size="lg"></spinner>
      </div>
      <div
        v-if="loadingState === LoadingState.ERRORED"
        class="text-secondary flex-center flex-grow-1"
      >
        Error while fetching details.
      </div>
      <template v-else-if="loadingState === LoadingState.LOADED">
        <initiative-content
          v-if="entityType === ENTITY_TYPE.INITIATIVE"
          :initiative="entity"
          :default-activity-filter="selectedActivityFilter"
          :query="{ event: eventId }"
          :key="notification.id"
          @delete-initiatives="$emit('delete-entity')"
        />
        <div
          class="container-wrapper"
          v-else-if="entityType === ENTITY_TYPE.KEY_RESULT"
          :key="notification.id"
        >
          <kr-activities
            :collapse-checkin="true"
            :kr-id="entity.id"
            :source="source"
            :event-to-focus="eventId"
            :show-kr-bar="true"
          />
        </div>
        <div
          class="container-wrapper py-1"
          v-else-if="entityType === ENTITY_TYPE.GOAL"
          :key="notification.id"
        >
          <archived-bar v-if="entity.isArchived" class="mb-2" />
          <goal-viewer
            v-if="entity"
            :goal-id="entity.id"
            :expand="true"
            :can-toggle-expansion="false"
            :show-alignment="true"
            :show-details-page-navigator="false"
            :render-krs-onload="true"
            :show-child-goals="false"
            :source="source"
            ref="goal"
            view-type="detail"
            @delete-goal="$emit('delete-entity')"
          ></goal-viewer>
          <goal-activity-section :event-to-focus="eventId" :goal="entity" />
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import ArchivedBar from '@/components/ArchivedBar'
import GoalActivitySection from '@/components/GoalActivitySection'
import GoalViewer from '@/components/GoalViewer'
import InitiativeContent from '@/components/initiative/InitiativeContent'
import KrActivities from '@/components/kr/KrActivities'
import Spinner from '@/components/Spinner'
import { FEED_FILTER_PREFERENCES, FEED_WITH_LOGS } from '@/constants/activity'
import { SOURCE } from '@/constants/analytics'
import { LoadingState } from '@/constants/common'
import { ENTITY_TYPE } from '@/constants/entities'
import getEntityTypeFromNotification from '@/utils/getEntityTypeFromNotification'
import showAPIError from '@/utils/showAPIError'
import * as R from 'ramda'
import { mapActions } from 'vuex'

/**
 * This is the details view that gets shown when a notification is clicked from inbox.
 * It shows the list of events. When launched, event for clicked notification is brought into viewport by default.
 *
 * @emits
 * - delete-entity: When goal/initiative is deleted
 */
export default {
  name: 'InboxModal',
  props: ['notification'],
  components: {
    InitiativeContent,
    Spinner,
    KrActivities,
    GoalActivitySection,
    GoalViewer,
    ArchivedBar
  },
  data() {
    return {
      ENTITY_TYPE,
      selectedActivityFilter: FEED_FILTER_PREFERENCES.find(R.propEq('id', FEED_WITH_LOGS)),
      loadingState: LoadingState.LOADING,
      LoadingState,
      source: SOURCE.INBOX
    }
  },
  computed: {
    eventId() {
      return R.path(['data', 'event', 'id'], this.notification)
    },
    entityType() {
      return getEntityTypeFromNotification(this.notification)
    },
    entity() {
      const entityTypeKeyMap = {
        [ENTITY_TYPE.GOAL]: 'goal',
        [ENTITY_TYPE.KEY_RESULT]: 'keyResult',
        [ENTITY_TYPE.INITIATIVE]: 'initiative'
      }
      return this.notification.data[entityTypeKeyMap[this.entityType]]
    }
  },
  methods: {
    ...mapActions(['getGoal', 'getInitiative']),
    async refreshEntityDetails() {
      this.loadingState = LoadingState.LOADING
      try {
        if (this.entityType === ENTITY_TYPE.INITIATIVE) {
          const initiative = this.$store.getters.initiativeById(this.entity.id)
          !initiative &&
            (await this.getInitiative({
              includeCustomFields: true,
              orgId: this.entity.organizationId,
              initiativeId: this.entity.id
            }))
        } else if (this.entityType === ENTITY_TYPE.KEY_RESULT) {
          const kr = this.$store.getters.keyResultById(this.entity.id)
          !kr &&
            (await this.getGoal({
              orgId: this.entity.organizationId,
              goalId: this.entity.goalId
            }))
        } else if (this.entityType === ENTITY_TYPE.GOAL) {
          const goal = this.$store.getters.goalById(this.entity.id)
          !goal &&
            (await this.getGoal({
              orgId: this.entity.organizationId,
              goalId: this.entity.id
            }))
        }
        this.loadingState = LoadingState.LOADED
      } catch (e) {
        showAPIError(e)
        this.loadingState = LoadingState.ERRORED
      }
    }
  },
  mounted() {
    this.refreshEntityDetails()
  },
  watch: {
    notification: 'refreshEntityDetails'
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/_styleConfigurations.scss';

.inbox-content-modal {
  @include scrollbarWidth($scrollSecondary);

  position: absolute;
  top: 20px;
  right: 10px;
  left: 7px;
  bottom: 20px;
  box-shadow: $cardShadow;
  background-color: $documentColor;
  overflow: auto;
  border-radius: 15px;
}
.container-wrapper {
  padding: 0 8px 0 10px;
}
/deep/ .initiative-row .title-col {
  left: 0;
  position: initial;
}
.backdrop {
  display: none;
}

/deep/ .goal-container .goal-viewer-wrapper {
  box-shadow: none !important;
}

@include media-breakpoint-down(md) {
  .inbox-content-modal {
    left: 0;
    right: 0;
    bottom: 0;
    top: 20%;
    z-index: 2;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }
  .backdrop {
    display: block;
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    top: 0;
    background-color: rgba(0, 0, 0, 0.8);
    z-index: 1;
    backdrop-filter: blur(2px);
  }
  /deep/ {
    .goal-container {
      .goal-checkin-history {
        display: none;
      }
      .kr-wrapper {
        padding-left: 0;
      }
    }
  }
}
</style>
